<template lang="pug">
.FNewsletter
  h4(v-html="t('newsletter_form__title')")
  FForm(
    v-slot="{ submit }"
    :initial-values="formInitialValues"
    @valid="handleValidForm"
  )
    FGrid(
      columns="1"
      gap="16"
    )
      FGridItem(span="12")
        FInput(
          name="email"
          :placeholder="t('newsletter_form__label__email')"
          :error-message="t('forms__error_feedback__email')"
          :rules="[required, email]"
          :error-color="errorColor"
        )
      FGridItem(span="12")
        FCheckbox(
          name="privacyPolicyAndTermsConsent"
          :error-message="t('forms__privacy_policy_and_terms_error')"
          :rules="[required]"
          text-color="secondary"
          :error-color="errorColor"
        )
          template(#label)
            .FNewsletter__legalText__label(
              v-html="t('forms__store_privacy_policy_and_terms_consent')"
            )
      FGridItem.FNewsletter__submitButton(:span="12")
        FButton(
          size="giant"
          color="neutral--light-5"
          background="secondary"
          :loading="loading"
          @click="submit"
        ) {{ t('newsletter_form__submit_button') }}

      FGridItem.FNewsletter__messages(:span="12")
        .FNewsletter__message--success(
          v-if="success"
          v-html="t('newsletter_form__success_message')"
        )
        .FNewsletter__message--error(
          v-else-if="error"
          v-html="t('newsletter_form__error_message')"
        )
</template>

<style lang="stylus">
.FNewsletter
  h4
    color var(--color--neutral--light-5)
    margin-bottom rem(48)

    +media-down('sm')
      text-align center
      font-size rem(24)
      line-height initial
      margin-bottom rem(20)

  .FNewsletter__consent__checkbox
    a:hover
      color var(--color--neutral--light-5)

.FNewsletter__legalText__label > a
  &:hover
    color var(--color--primary--light-2)

.FNewsletter__submitButton
  display flex
  justify-content center
  max-width rem(448)
  margin rem(16) auto 0

  .FButton
    width 100%

  +media-down('sm')
    max-width max-content
    margin rem(4) auto 0

.FNewsletter__messages
  text-align center

.FNewsletter__message--success
  color var(--color--success--light-1)

.FNewsletter__message--error
  color var(--color--warning--light-1)
</style>

<script setup lang="ts">
import { email, required } from '@vee-validate/rules';

export interface FNewsletterProps {
  /**
   * Color of the error (outline of the input and error text)
   */
  errorColor?: Color;
}

withDefaults(defineProps<FNewsletterProps>(), {
  errorColor: 'primary',
});

const { t } = useI18n();

const formInitialValues: Record<string, unknown> = {
  email: '',
  storeAndProcessConsent: false,
};

const config = useRuntimeConfig();
const { success, error, loading, submitForm } = useHubspotForm();

function handleValidForm(values: typeof formInitialValues): void {
  submitForm(config.public.hubspotNewsletterFormId, values, {
    storeAndProcess: {
      field: 'privacyPolicyAndTermsConsent',
      text: t('forms__store_process_consent'),
    },
    marketing: [
      {
        field: '', // tacit consent
        text: t('newsletter_form__title'),
        id: config.public.hubspotMarketingSubscriptionId,
      },
    ],
  });
}
</script>
